'use client';

import { Fragment, useEffect } from 'react';

import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListVirtualPageFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListVirtualPageFragmentFragmentDoc';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

import { usePaging } from '../../hooks/usePaging';
import { SearchAdServerIntegration } from '../SearchAdServerIntegration/SearchAdServerIntegration.client';
import { SearchProductCard } from '../SearchProductCard/SearchProductCard.client';
import { SpaProductCard } from '../SpaProductCard/SpaProductCard.client';
import { SpaBeaconImage } from '../SpaBeaconImage/SpaBeaconImage';

import { useGlycerinListingsEvents } from './useGlycerinListingsEvents';
import { useSpaTracking } from './useSpaTracking';
import { usePRankProductTracking } from '../../hooks/usePRankTracking';

// Banner appears after this many products (including sponsored products on first page)
const ADS_BANNER_POSITION_THRESHOLD = 12;

/* GraphQL */ `
  fragment ProductListVirtualPageFragment on SearchProductResult {
    sponsoredProducts {
      provider
      tracking {
        onLoad
        onView
        adId
      }
      items {
        styleId
        primaryVariationGroup {
          sku
        }
        ...SpaProductCardItemFragment
      }
    }
    summary {
      totalResultCount
    }
    items {
      styleId
      ...SearchProductCardItemFragment
    }
    ...UsePagingFragment
    ...SearchProductCardResultFragment
    ...SpaProductCardResultFragment
    ...ProductGridSpaTrackingFragment
    ...GlycerinListingsEventsResultFragment
  }
`;

export type ProductListVirtualPageProps = {
  maskedResult: FragmentType<typeof ProductListVirtualPageFragmentFragmentDoc>;
  /**
   * Current page number, starting at 1 and incrementing by 1 for each page (for infinite scrolling, where usually there is no page number exposed to the user)
   */
  pageNumber: number;
  /**
   * Event fired when any card in the grid first becomes visible
   */
  onProductFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
  SponsoredProductCardComponent?: typeof SpaProductCard;
  StandardProductCardComponent?: typeof SearchProductCard;
  // NOTE this is only here for storybook compat for now, because the wishlist button can't render in storybook due to technical issues with MUI icons
  hideWishlistButton?: boolean;
  isFirstVisiblePage?: boolean;
  addLCPWorkaround?: boolean;
  /**
   * Major product group for determining ad eligibility
   */
  majorMkz?: number;
  /**
   * Enables pRank tracking for product listings.
   * This should be enabled specifically on Category Pages where pRank A/B testing is active.
   * When disabled (default), no pRank tracking events are dispatched.
   */
  enablePRankTracking?: boolean;
};

/**
 * ProductGrid component based on https://mui.com/material-ui/react-grid/
 * */
export const ProductListVirtualPage = ({
  maskedResult,
  pageNumber,
  onProductFirstBecameVisible,
  SponsoredProductCardComponent = SpaProductCard,
  StandardProductCardComponent = SearchProductCard,
  hideWishlistButton,
  isFirstVisiblePage,
  addLCPWorkaround,
  majorMkz,
  enablePRankTracking = false,
}: ProductListVirtualPageProps) => {
  const result = unmask<typeof ProductListVirtualPageFragmentFragmentDoc>(maskedResult);
  const { pageSize } = usePaging(result);
  const itemPosition = (pageNumber - 1) * pageSize + 1;
  const { totalResultCount } = result.summary;

  const { search } = useConfig();

  const { dispatchGlycerinListingsEvent } = useGlycerinListingsEvents({
    maskedResult: result,
    pageNumber,
    odoscopeToken: search?.odoscopeToken,
  });
  const { dispatchSpaTrackingEvent: dispatchSpaTracking } = useSpaTracking({
    maskedResult: result,
  });

  // pRank A/B test: Mark all displayed products as test-relevant
  // We only enable this on Category Pages (enablePRankTracking=true)
  usePRankProductTracking(result.items, enablePRankTracking);

  useEffect(() => {
    dispatchGlycerinListingsEvent();
    dispatchSpaTracking();
    // TODO SEARCH-3512 figure out if this works with correct dependencies as well
    // The display event should be fired only once, when the virtual page is initially displayed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prerenderedProductCount = result.sponsoredProducts ? 4 : 8;
  // NOTE for now it is cleared with stakeholders to just use a fixed number of sponsored products
  // should this change in the future, it will be complex to always show "one row" again, because the layout is now css-based and the beacons must not fire when the card is not actually visible
  const sponsoredProductCount = 4;

  const hasSponsoredProducts = (result.sponsoredProducts?.items?.length ?? 0) > 0;
  const actualSponsoredCount = hasSponsoredProducts ? sponsoredProductCount : 0;

  // Banner position in items array:
  // - First page: after 12th product (threshold - sponsored count, since sponsored are separate)
  // - Other pages: after 12th product (threshold, no sponsored products on subsequent pages)
  const bannerPositionInPage =
    pageNumber === 1
      ? ADS_BANNER_POSITION_THRESHOLD - actualSponsoredCount
      : ADS_BANNER_POSITION_THRESHOLD;

  return (
    <>
      {result.sponsoredProducts && (
        <>
          {result.sponsoredProducts.tracking.onLoad && (
            <SpaBeaconImage src={result.sponsoredProducts.tracking.onLoad} name="spa:onLoad" />
          )}
          {result.sponsoredProducts.tracking.onView && (
            <SpaBeaconImage src={result.sponsoredProducts.tracking.onView} name="spa:onView" />
          )}
        </>
      )}

      {result.sponsoredProducts?.items?.slice(0, sponsoredProductCount).map((item, index) => (
        <SponsoredProductCardComponent
          key={`spa-${item.styleId}-${item.primaryVariationGroup.sku}`} // sponsored cards can have the same styleId, so we need to include the sku in the key
          maskedResult={result}
          maskedItem={item}
          pageNumber={pageNumber}
          onFirstBecameVisible={onProductFirstBecameVisible}
          priority={isFirstVisiblePage && index === 0}
          provider={result.sponsoredProducts?.provider ?? undefined}
        />
      ))}

      {result.items.map((item, index) => (
        <Fragment key={item.styleId}>
          <StandardProductCardComponent
            aria-setsize={totalResultCount}
            aria-posinset={itemPosition + index}
            maskedItem={item}
            maskedResult={result}
            pageNumber={pageNumber}
            indexInPage={index}
            alwaysRender={index < prerenderedProductCount}
            onFirstBecameVisible={onProductFirstBecameVisible}
            hideWishlistButton={hideWishlistButton}
            priority={isFirstVisiblePage && !hasSponsoredProducts && index === 0}
            // Only add LCP workaround to the very first product card on the first visible page
            addLCPWorkaround={addLCPWorkaround && index === 0}
          />
          {index + 1 === bannerPositionInPage && (
            <ErrorBoundary>
              <SearchAdServerIntegration
                slotType="banner"
                mountPointId={`div-ad-server-mba-banner-${pageNumber}`}
                majorMkz={majorMkz}
              />
            </ErrorBoundary>
          )}
        </Fragment>
      ))}
    </>
  );
};
